import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { BackButton } from '@/components/common/BackButton';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader, type PageSearch } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Skeleton } from '@/components/ui/skeleton';
import { useHistoryBack } from '@/hooks/useHistoryBack';

interface DetailTableLoadMore {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: boolean;
}

interface DetailTable<TRow> {
  rows: TRow[];
  columns: ColumnDef<TRow, unknown>[];
  isLoading: boolean;
  error: Error | null;
  errorTitle: string;
  onRetry: () => void;
  stickyColumnId: string;
  emptyMessage: string;
  emptyHint?: string | undefined;
  isRowHighlighted?: ((row: TRow) => boolean) | undefined;
  getRowClassName?: ((row: TRow) => string | undefined) | undefined;
  loadMoreProps?: DetailTableLoadMore | undefined;
}

interface DetailTableLayoutProps<TRow> {
  title: string | undefined;
  titleAddon?: ReactNode;
  search?: PageSearch | undefined;
  actions?: ReactNode;
  onNoHistory: () => void;
  summary?: ReactNode;
  stats?: LabeledStat[] | undefined;
  tableTabs?: ReactNode;
  table: DetailTable<TRow>;
}

export function DetailTableLayout<TRow>({
  title,
  titleAddon,
  search,
  actions,
  onNoHistory,
  summary,
  stats = [],
  tableTabs,
  table,
}: DetailTableLayoutProps<TRow>) {
  const goBack = useHistoryBack(onNoHistory);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        leading={<BackButton onClick={goBack} />}
        title={title ?? <Skeleton className="inline-block h-7 w-56 align-middle" />}
        titleAddon={titleAddon}
        search={search}
        actions={actions}
      />

      {summary}

      {table.error ? (
        <ErrorState
          error={table.error}
          fallbackTitle={table.errorTitle}
          onRetry={table.onRetry}
          fill
        />
      ) : (
        <>
          {stats.length > 0 ? <StatCardGrid stats={stats} isLoading={table.isLoading} /> : null}
          {tableTabs}
          <DataTable
            data={table.rows}
            columns={table.columns}
            isLoading={table.isLoading}
            className="lg:flex-1"
            stickyColumnId={table.stickyColumnId}
            emptyMessage={table.emptyMessage}
            emptyHint={table.emptyHint}
            isRowHighlighted={table.isRowHighlighted}
            {...(table.getRowClassName && { getRowClassName: table.getRowClassName })}
            {...table.loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
